"use client";

import { useStyles } from "./styles";
import IdyllicBaseIcon from "@/assets/models/idyllic-base-icon.webp";
import IdyllicHDIcon from "@/assets/models/idyllic-hd-icon.webp";
import IdyllicHDRPlusIcon from "@/assets/models/idyllic-hdr-plus-icon.webp";
import AutoModelIcon from "@/assets/models/auto-model-icon.webp";
import NSFW_FastIcon from "@/assets/models/nsfw-fast-icon.webp";
import Imagen4Icon from "@/assets/models/imagen-4-icon.webp";
import NSFW_Icon from "@/assets/models/nsfw-icon.webp";
import GPT_Icon from "@/assets/models/gpt-icon.webp";
import FluxProIcon from "@/assets/models/flux-pro-icon.webp";
import Imagen4UltraIcon from "@/assets/models/imagen-4-ultra-icon.webp";
import Grok2Icon from "@/assets/models/grok-2-icon.webp";

const ASPECT_RATIO_DIMENSIONS = [
  { ratio: "1:1", x: 1, y: 1, width: 13, height: 13 },
  { ratio: "2:3", x: 3.5, y: 1.5, width: 8, height: 12 },
  { ratio: "9:16", x: 4, y: 1.3, width: 7, height: 12.4 },
  { ratio: "16:9", x: 0.5, y: 3.6, width: 14, height: 7.9 },
  { ratio: "3:4", x: 3, y: 1.5, width: 9, height: 12 },
  { ratio: "4:3", x: 1.5, y: 3, width: 12, height: 9 },
  { ratio: "3:2", x: 1, y: 3.2, width: 13, height: 8.7 },
  { ratio: "21:9", x: 0.5, y: 4.5, width: 14, height: 6 },
];

function getAspectRatioDimensions(ratio: string) {
  const aspectRatioDimensions = ASPECT_RATIO_DIMENSIONS.find(
    (r) => r.ratio === ratio
  );

  if (!aspectRatioDimensions) {
    return { ratio: "1:1", x: 1, y: 1, width: 13, height: 13 };
  }

  return aspectRatioDimensions;
}

function getModelUsedLabel(model: string): string {
  switch (model) {
    case "hd-turbo":
      return "Idyllic Base";
    case "hd":
      return "Idyllic HD";
    case "hd+":
      return "Idyllic HDR+";
    case "auto":
      return "Auto-select";
    case "nsfw-fast":
    case "flux-schnell-generate-image":
      return "NSFW Fast";
    case "imagen-4":
    case "google-imagen-4-generate-image":
      return "Imagen 4";
    case "nsfw":
    case "flux-dev-generate-image":
      return "NSFW";
    case "gpt":
    case "gpt-image-1-generate-image":
      return "GPT Image";
    case "flux-pro":
    case "flux-pro-1.1-generate-image":
      return "Flux Pro";
    case "imagen-4-ultra":
    case "google-imagen-4-ultra-generate-image":
      return "Imagen 4 Ultra";
    case "grok-2":
    case "grok-2-image-generate-image":
      return "Grok 2";
    case "upscale":
      return "Upscale";
    case "inpaint":
      return "Inpaint";
    case "enhance":
      return "Enhance";
    case "remix":
      return "Remix";
    case "turbo":
      return "Turbo";
    case "ai-text":
      return "AI Text";
    default:
      return model;
  }
}

function getModelUsedIcon(model: string): string {
  switch (model) {
    case "hd-turbo":
      return IdyllicBaseIcon.src;
    case "hd":
      return IdyllicHDIcon.src;
    case "hd+":
      return IdyllicHDRPlusIcon.src;
    case "auto":
      return AutoModelIcon.src;
    case "nsfw-fast":
    case "flux-schnell-generate-image":
      return NSFW_FastIcon.src;
    case "imagen-4":
    case "google-imagen-4-generate-image":
      return Imagen4Icon.src;
    case "nsfw":
    case "flux-dev-generate-image":
      return NSFW_Icon.src;
    case "gpt":
    case "gpt-image-1-generate-image":
      return GPT_Icon.src;
    case "flux-pro":
    case "flux-pro-1.1-generate-image":
      return FluxProIcon.src;
    case "imagen-4-ultra":
    case "google-imagen-4-ultra-generate-image":
      return Imagen4UltraIcon.src;
    case "grok-2":
    case "grok-2-image-generate-image":
      return Grok2Icon.src;
    case "turbo":
      return IdyllicBaseIcon.src;
    case "enhance":
    case "remix":
    case "inpaint":
    case "upscale":
    case "ai-text":
      return IdyllicBaseIcon.src;
    default:
      return AutoModelIcon.src;
  }
}

interface GenerationConfigProps {
  modelUsed: string;
  aspectRatio: string;
}

const GenerationConfig: React.FC<GenerationConfigProps> = ({
  modelUsed,
  aspectRatio,
}) => {
  const { classes } = useStyles();
  const modelIcon = getModelUsedIcon(modelUsed);
  const modelLabel = getModelUsedLabel(modelUsed);
  const { x, y, width, height } = getAspectRatioDimensions(aspectRatio);

  return (
    <div className={classes.container}>
      <div className={classes.modelBadge}>
        <img src={modelIcon} alt={modelLabel} className={classes.modelIcon} />{" "}
        {modelLabel}
      </div>
      <div className={classes.aspectRatioBadge}>
        <div className={classes.aspectRatioContainer}>
          <svg
            viewBox="0 0 15 15"
            fill="none"
            stroke="currentColor"
            strokeWidth="1"
          >
            <rect x={x} y={y} width={width} height={height} rx="1" />
          </svg>
        </div>
        <span>{aspectRatio} Ratio</span>
      </div>
    </div>
  );
};

export default GenerationConfig;
